import { useTranslation } from 'react-i18next'
import { Copy01Icon } from '@untitledui-icons/react/line'

interface WidgetEmbedCellProps {
  assistantName: string
  /** The assistant's snippet, or null while it has no widget key yet. */
  snippet: string | null
  onView: () => void
  onCopy: () => void
}

/**
 * The embed code cell: a fixed-width chip that previews the snippet, opens it in
 * full, and copies it. Assistants without a snippet show a dash, because the
 * code only exists once WhatsApp is connected.
 */
export function WidgetEmbedCell({ assistantName, snippet, onView, onCopy }: WidgetEmbedCellProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.assistants'

  if (!snippet) {
    return (
      <>
        <span
          aria-hidden="true"
          className="inline-block h-[1.5px] w-6 bg-gray-500 dark:bg-gray-600"
        />
        <span className="sr-only">{t(`${base}.embed_empty`)}</span>
      </>
    )
  }

  return (
    <span className="bg-gray-25 inline-flex h-7 w-30 items-center gap-2 rounded-[2px] border border-gray-200 p-1 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600">
      <button
        type="button"
        onClick={onView}
        title={t(`${base}.view_embed`, { assistant: assistantName })}
        className="min-w-0 flex-1 cursor-pointer truncate text-left font-mono text-xs tracking-[-1px] text-gray-600 dark:text-gray-300"
      >
        {snippet}
      </button>
      <button
        type="button"
        onClick={onCopy}
        aria-label={t(`${base}.copy_embed`, { assistant: assistantName })}
        title={t('resources.generic.copy')}
        className="flex shrink-0 cursor-pointer rounded-[2px] text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
      >
        <Copy01Icon className="size-4" />
      </button>
    </span>
  )
}
